import React, { FC } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { useSelector } from 'react-redux';
import i18n from 'i18n-js';

import { scale } from '@components/frame';
import { isWeb, isPhone } from '@helpers';
import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';
import ColorsWrapper from '@styles/colors';

import Search from '@components/search';
import { SCREEN_PADDING_HORIZONTAL } from '@v2/pages/common/constants/base.constants';
import { ColorFunction } from '@styles/colors/types';
import { ImageDocumentStoreItem, RetrievedDocumentReferenceLink } from '@v2/components/imageHandling/types/types';
import { useManageSearchAttachmentPopup } from './hooks/useManageSearchAttachmentPopup';
import { selectUpdatingImageMetadataInProgress } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import AttachConfirmationModal from './AttachConfirmationModal';

export interface SearchAttachmentPopupProps {
  C: ColorFunction;
  hide: () => void;
  attachImageId: number;
  fileName: string;
  imageDocument: ImageDocumentStoreItem;
  onAttachSuccess?: (attachmentData: {
    documentId: number;
    documentTags: string[];
    documentLinks: Partial<RetrievedDocumentReferenceLink>[];
  }) => void;
  maxHeight: number;
}

const SearchAttachmentPopup: FC<SearchAttachmentPopupProps> = (props) => {
  const { C, hide, attachImageId, fileName, onAttachSuccess, maxHeight, imageDocument, ...searchProps } = props;

  const updatingImageMetadataInProgress = useSelector(selectUpdatingImageMetadataInProgress);

  const {
    fixedPopupWidth,
    handleCancelAttachment,
    handleConfirmAttachment,
    handleItemSelect,
    popupWidth,
    selectedItem,
    setPopupWidth,
    showConfirmationModal,
  } = useManageSearchAttachmentPopup({ attachImageId, hide, onAttachSuccess, imageDocument });

  return (
    <View
      style={{
        width: '100%',
        height: isPhone ? '100%' : maxHeight,
        backgroundColor: C(isWeb ? 'Background/Light/Panel' : 'Background/Light/Modal Highlight'),
        borderRadius: isPhone ? 0 : scale(16),
        flexDirection: 'column',
        overflow: 'visible',
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          paddingHorizontal: isPhone ? scale(SCREEN_PADDING_HORIZONTAL) : scale(20),
          paddingTop: scale(isPhone ? 16 : 22),
          paddingBottom: scale(0),
          zIndex: 10000,
          backgroundColor: C(isWeb ? 'Background/Light/Panel' : 'Background/Light/Modal Highlight'),
        }}
      >
        <View style={{ flex: 1 }}>
          <Text
            style={[
              font['Roboto'],
              {
                color: C('Label/Light/Primary'),
                fontWeight: 700,
                fontSize: isPhone ? scale(18) : scale(26),
              },
            ]}
          >
            {isPhone ? i18n.t('Search Item To Attach To') : `${i18n.t('Search Item To Attach To')} ${fileName}`}
          </Text>
          {isPhone && fileName && (
            <Text
              style={[
                font['Roboto'],
                {
                  color: C('Label/Light/Secondary'),
                  fontWeight: 400,
                  fontSize: scale(12),
                  marginTop: scale(4),
                  lineHeight: scale(16),
                },
              ]}
            >
              {fileName}
            </Text>
          )}
        </View>
        {isPhone && (
          <View style={{ flexDirection: 'column', alignItems: 'flex-end', marginTop: scale(2) }}>
            <TouchableOpacity onPress={hide} style={{ padding: scale(4) }} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
              <Svg icon="close" width={scale(20)} height={scale(20)} color="Link/Light/Primary" />
            </TouchableOpacity>
          </View>
        )}
        {!isPhone && (
          <TouchableOpacity onPress={hide} style={{ padding: scale(4) }} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
            <Svg icon="close" width={scale(20)} height={scale(20)} color="Link/Light/Primary" />
          </TouchableOpacity>
        )}
      </View>

      {/* Search Content */}
      <View
        style={{
          flex: 1,
          overflow: 'visible',
        }}
        onLayout={(event) => {
          const { width } = event.nativeEvent.layout;
          // Only update width dynamically for phones, tablets use fixed width
          if (!fixedPopupWidth) {
            setPopupWidth(width);
          }
        }}
      >
        <Search
          {...searchProps}
          route={{
            params: {
              attachMode: true,
              attachImageId,
            },
          }}
          tableNamePrefix="attachment"
          onItemSelect={handleItemSelect}
          isPopupMode={true}
          popupWidth={popupWidth}
        />
      </View>

      {/* Attach Confirmation Modal */}
      {selectedItem && (
        <AttachConfirmationModal
          visible={showConfirmationModal}
          onClose={handleCancelAttachment}
          onConfirm={handleConfirmAttachment}
          itemName={selectedItem.itemName}
          itemType={selectedItem.itemType}
          updateInProgress={updatingImageMetadataInProgress}
        />
      )}
    </View>
  );
};

export default ColorsWrapper(SearchAttachmentPopup);
